import { type SQL, and, or } from 'drizzle-orm'

type SqlConditionOperator = ({
  left,
  right,
}: {
  readonly left: SQL<unknown>
  readonly right: SQL<unknown>
}) => SQL<unknown> | undefined

/**
 * SQL 条件を平衡木として結合し、SQLite の式ツリー深度を抑える。
 * @param params SQL 条件一覧と二項演算子。
 * @returns 結合した SQL 条件。条件が空の場合は undefined。
 */
const combineSqlConditions = ({
  conditions,
  operator,
}: {
  readonly conditions: readonly SQL<unknown>[]
  readonly operator: SqlConditionOperator
}): SQL<unknown> | undefined => {
  if (conditions.length === 0) {
    return undefined
  }
  if (conditions.length === 1) {
    return conditions[0]
  }

  const middle = Math.ceil(conditions.length / 2)
  const left = combineSqlConditions({
    conditions: conditions.slice(0, middle),
    operator,
  })
  const right = combineSqlConditions({
    conditions: conditions.slice(middle),
    operator,
  })
  if (left === undefined || right === undefined) {
    throw new Error('SQL condition branch should not be undefined')
  }

  return operator({ left, right })
}

/**
 * SQL 条件一覧を平衡化した AND 条件として結合する。
 * @param params SQL 条件一覧。
 * @returns 結合した SQL 条件。条件が空の場合は undefined。
 */
export const andSqlConditions = ({
  conditions,
}: {
  readonly conditions: readonly SQL<unknown>[]
}): SQL<unknown> | undefined =>
  combineSqlConditions({
    conditions,
    operator: ({ left, right }) => and(left, right),
  })

/**
 * SQL 条件一覧を平衡化した OR 条件として結合する。
 * @param params SQL 条件一覧。
 * @returns 結合した SQL 条件。条件が空の場合は undefined。
 */
export const orSqlConditions = ({
  conditions,
}: {
  readonly conditions: readonly SQL<unknown>[]
}): SQL<unknown> | undefined =>
  combineSqlConditions({
    conditions,
    operator: ({ left, right }) => or(left, right),
  })
